import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import PageHeader from '../Elements/UI/PageHeader'
import EmptyState from '../Elements/UI/EmptyState'
import FeedSkeleton from '../Elements/UI/FeedSkeleton'
import PaymentPlan from './SmallComponents/PaymentPlan'
import PaymentDueAlert from '../Elements/UI/PaymentDueAlert'
import Editpaymentmodal from '../Modals/Editpaymentmodal'
import {
  fetchMyPayments,
  fetchAllPayments,
  refreshMyPayments,
  summarize,
  hasDue,
  formatINR,
  formatDate
} from '../../features/payments/memberPayments'

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'due', label: 'Outstanding' },
  { id: 'advance', label: 'Advance' },
  { id: 'clear', label: 'No dues' }
]

const initials = name =>
  (name || '?').split(/[\s&,]+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('')

function PaymentDetails({ userAccess = [] }) {
  const isAdmin = Array.isArray(userAccess) && userAccess.includes('ListUser')

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [asOn, setAsOn] = useState(null)
  const [entries, setEntries] = useState([])
  const [selected, setSelected] = useState(null) // admin: entry being viewed
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [editEntry, setEditEntry] = useState(null) // admin: entry open in the edit dialog

  useEffect(() => {
    const load = isAdmin ? fetchAllPayments() : fetchMyPayments()
    load
      .then(res => {
        setAsOn(res.asOn)
        setEntries(res.entries)
      })
      .catch(err => {
        console.error('Failed to load payments', err)
        setError('Could not load payment details. Please try again later.')
      })
      .finally(() => setLoading(false))
  }, [isAdmin])

  const mySummary = useMemo(() => summarize(entries), [entries])
  const adminTotals = useMemo(() => summarize(entries), [entries])
  const withDues = useMemo(() => entries.filter(hasDue), [entries])

  const replaceEntry = updated => {
    if (!updated) return
    setEntries(prev => prev.map(e => (e.id === updated.id ? updated : e)))
    setSelected(prev => (prev && prev.id === updated.id ? updated : prev))
    // keep the member's alert / sidebar badge in sync with the latest figures
    refreshMyPayments().catch(() => {})
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return entries.filter(e => {
      if (q && !e.name.toLowerCase().includes(q) && !e.flatNo.includes(q)) return false
      if (filter === 'due') return hasDue(e)
      if (filter === 'advance') return e.advance > 0
      if (filter === 'clear') return !hasDue(e)
      return true
    })
  }, [entries, search, filter])

  const asOnText = asOn ? `As on ${formatDate(asOn)} · from audited books of accounts` : ''

  return (
    <div id="appCapsule">
      <div className="section">
        <div className="bl-container px-0">
          <PageHeader
            title={isAdmin ? 'Members’ payment details' : 'Details of payment'}
            subtitle={isAdmin
              ? 'Additional area / additional flat contributions of all members'
              : 'Your additional area / additional flat contributions'}
          />
          {asOnText && <p className="bl-pay__ason">{asOnText}</p>}

          {loading && <FeedSkeleton count={2} label="Loading payment details" />}

          {!loading && error && (
            <EmptyState icon="cloud-offline-outline" title="Something went wrong" text={error} />
          )}

          {/* ----- Member view: only their own entries (filtered by the server) ----- */}
          {!loading && !error && !isAdmin && (
            entries.length === 0 ? (
              <EmptyState
                icon="wallet-outline"
                title="No additional area payments"
                text="Your flat has no additional area or additional flat contribution on record."
              />
            ) : (
              <>
                <PaymentDueAlert showLink={false} />
                {entries.length > 1 && (
                  <div className="card bl-pay__summary mb-3">
                    <div className="bl-pay__stats mb-0">
                      <div><span className="bl-pay__label">Total payable</span><span className="bl-pay__value">{formatINR(mySummary.totalPayable)}</span></div>
                      <div><span className="bl-pay__label">Total received</span><span className="bl-pay__value">{formatINR(mySummary.totalReceived)}</span></div>
                      <div><span className="bl-pay__label">Outstanding</span><span className={`bl-pay__value ${mySummary.outstanding >= 1 ? 'text-danger' : ''}`}>{formatINR(mySummary.outstanding)}</span></div>
                    </div>
                  </div>
                )}
                {entries.map(entry => (
                  <PaymentPlan
                    key={entry.id}
                    entry={entry}
                    onChange={replaceEntry}
                  />
                ))}
              </>
            )
          )}

          {/* ----- Admin view: all members ----- */}
          {!loading && !error && isAdmin && !selected && (
            <>
              <div className="bl-pay-totals">
                <div className="card"><span className="bl-pay__label">Entries</span><span className="bl-pay__value">{entries.length}</span></div>
                <div className="card"><span className="bl-pay__label">Total payable</span><span className="bl-pay__value">{formatINR(adminTotals.totalPayable)}</span></div>
                <div className="card"><span className="bl-pay__label">Received</span><span className="bl-pay__value text-success">{formatINR(adminTotals.totalReceived)}</span></div>
                <div className="card"><span className="bl-pay__label">Outstanding</span><span className="bl-pay__value text-danger">{formatINR(adminTotals.outstanding)}</span></div>
                <div className="card"><span className="bl-pay__label">Advance</span><span className="bl-pay__value">{formatINR(adminTotals.advance)}</span></div>
              </div>

              {withDues.length > 0 && (
                <div className="bl-alert bl-alert--danger" role="alert">
                  <div className="bl-alert__icon" aria-hidden="true"><ion-icon name="alert-circle-outline"></ion-icon></div>
                  <div className="bl-alert__body">
                    <strong>{withDues.length} {withDues.length === 1 ? 'entry has' : 'entries have'} a pending payment</strong>
                    <span>Total outstanding {formatINR(adminTotals.outstanding)}.</span>
                    {filter !== 'due' && (
                      <button type="button" className="btn btn-link p-0 bl-alert__link" onClick={() => setFilter('due')}>
                        Show only members with dues
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="bl-pay-toolbar">
                <div className="bl-search flex-grow-1">
                  <ion-icon name="search-outline" aria-hidden="true" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--bl-muted)' }}></ion-icon>
                  <input
                    type="search"
                    className="form-control"
                    placeholder="Search name or flat (e.g. 05/12)"
                    aria-label="Search members"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </div>
                <div className="bl-segmented" role="tablist" aria-label="Filter members">
                  {FILTERS.map(f => (
                    <button key={f.id} type="button" role="tab" aria-selected={filter === f.id} className={filter === f.id ? 'active' : ''} onClick={() => setFilter(f.id)}>
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-muted small mb-2">{filtered.length} of {entries.length} entries</p>

              {filtered.length === 0 ? (
                <EmptyState icon="search-outline" title="No matching members" text="Try a different name, flat number or filter." />
              ) : (
                <ul className="bl-pay-members">
                  {filtered.map(entry => (
                    <li key={`${entry.flatNo}-${entry.sr}`}>
                      <button type="button" className="card bl-pay-member card-interactive" onClick={() => setSelected(entry)}>
                        <span className="bl-avatar bl-avatar--lg" aria-hidden="true">{initials(entry.name)}</span>
                        <span className="bl-pay-member__text">
                          <span className="bl-pay-member__name">{entry.name}</span>
                          <span className="bl-pay-member__meta">
                            {entry.flatNo} · {entry.type} · {entry.areaSqft} sq.ft · Payable {formatINR(entry.totalPayable)}
                          </span>
                        </span>
                        <span className="bl-pay-member__status">
                          {hasDue(entry) ? (
                            <span className="bl-chip bl-chip--danger">Due {formatINR(entry.outstanding)}</span>
                          ) : entry.advance > 0 ? (
                            <span className="bl-chip bl-chip--info">Advance {formatINR(entry.advance)}</span>
                          ) : (
                            <span className="bl-chip bl-chip--success">No dues</span>
                          )}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {!loading && !error && isAdmin && selected && (
            <>
              <button type="button" className="btn btn-link px-0 mb-2" onClick={() => setSelected(null)}>
                <ion-icon name="chevron-back-outline" aria-hidden="true"></ion-icon>
                All members
              </button>
              <div className="bl-pay-member__head">
                <h2 className="h3 mb-0">{selected.name}</h2>
                <span className="text-muted small">Flat {selected.flatNo} · Sr. {selected.sr}</span>
              </div>
              <PaymentPlan
                entry={selected}
                isAdmin
                onChange={replaceEntry}
                onEdit={setEditEntry}
              />
              <Editpaymentmodal
                show={!!editEntry}
                entry={editEntry}
                onClose={() => setEditEntry(null)}
                onSaved={replaceEntry}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = ({ auth }) => ({
  userAccess: auth.userAccess
})

export default connect(mapStateToProps)(PaymentDetails)
